import type { ChangeEvent } from "react";
import styled from "styled-components";

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  font-family: "Inter", sans-serif;
`;

const StyledCheckboxLabel = styled.label`
  position: relative;
  display: flex !important;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5em;
  color: var(--text);
  cursor: pointer;
  @media only screen and (max-width: 768px) {
    font-size: 14px;
  }
`;

const StyledCustomCheckbox = styled.span<{ isChecked: boolean }>`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 40px;
  margin-right: 15px;
  cursor: pointer;
  flex-shrink: 0;
  background-color: ${({ isChecked }) =>
    isChecked ? "var(--radio-background-selected)" : "var(--color-white)"};
  border: 1px solid;
  border-color: ${({ isChecked }) =>
    isChecked ? "var(--radio-background-selected)" : "rgba(0, 0, 0, 0.3)"};
  border-radius: 2px;
  transition: background-color 0.4s, border-color 0.4s, box-shadow 0.2s ease;
  aspect-ratio: 1/1;

  &::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 48%;
    /* ~half the box edge — reads clearly at 40×40 */
    width: 11px;
    height: 20px;
    border: solid var(--radio-inner-background-selected);
    border-width: 0 3px 3px 0;
    transform: translate(-50%, -58%) rotate(45deg) scale(0);
    transition: transform 0.2s ease;
  }
`;

const StyledCheckboxInput = styled.input`
  position: absolute;
  width: 0.1px;
  height: 0.1px;
  z-index: -1;
  opacity: 0;
  transition: 0.4s;

  &:focus {
    outline: none;
  }

  &:hover + span,
  &:focus + span {
    border-color: var(--primary);
  }

  &:focus-visible + span {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px var(--color-white), 0 0 0 4px var(--primary);
  }

  &:checked + ${StyledCustomCheckbox}::after {
    transform: translate(-50%, -58%) rotate(45deg) scale(1);
  }
`;

const CheckboxesWidget = (props: any) => {
  const { id, schema, onChange, onBlur, value } = props;

  if (!schema?.items || !schema?.items?.enum) {
    return null;
  }

  const { enum: enumValues, enumNames } = schema.items;

  const checked = new Set(value || []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!onChange || typeof onChange !== "function") return;

    const value = e.target.value;
    if (checked.has(value)) {
      checked.delete(value);
    } else {
      checked.add(value);
    }

    onChange(Array.from(checked));
  };

  return (
    <CheckboxGroup id={id} onBlur={onBlur}>
      {enumValues.map((item: string, i: number) => {
        const label = enumNames?.[i] || item;
        const isChecked = checked.has(item);

        return (
          <StyledCheckboxLabel key={item}>
            <StyledCheckboxInput
              id={item}
              type="checkbox"
              value={item}
              name={id}
              checked={isChecked}
              onChange={handleChange}
            />
            <StyledCustomCheckbox isChecked={isChecked} />
            {label}
          </StyledCheckboxLabel>
        );
      })}
    </CheckboxGroup>
  );
};

export default CheckboxesWidget;
